/**
 * chatVanillaMenu.ts — context menu рендерится в чистом JavaScript, минуя React.
 *
 * Зачем: React 18 + createPortal на iOS PWA standalone задерживает доставку
 * click-событий и unmount портала на 1-3 секунды. Vanilla JS DOM-менеджмент
 * полностью обходит эту проблему. CSS-классы те же — дизайн идентичен.
 *
 * API: openContextMenu(opts) / closeContextMenu(). DOM создаётся один раз,
 * затем только показывается/скрывается + обновляется контент.
 */
import type { ChatMessage, ChatAttachment } from "@/api/dashboard";
import twemoji from "twemoji";
import { emojiToTwemojiCode } from "@/components/Emoji";

/** Прямо HTML <img> для emoji — без native flash при render. */
function emojiImgHtml(emo: string): string {
  const cp = emojiToTwemojiCode(emo);
  return `<img class="emoji emoji-img" draggable="false" alt="" src="/twemoji/svg/${cp}.svg"/>`;
}

export interface VanillaMenuOpenOpts {
  message: ChatMessage;
  attachmentId?: number;
  position: {
    pillTop: number;
    pillLeft: number;
    menuTop: number;
    menuLeft: number;
    liftOffset: number;
    menuMaxHeight: number;
  };
  isMobile: boolean;
  canEditMine: boolean;
  canDelete: boolean;
  canRestore: boolean;
  quickEmoji: string[];
  /** Захваченное выделение в момент открытия меню. Если задано —
   *  показываем «Цитировать выделенное». */
  quotedText?: string;
  callbacks: {
    onReact: (emoji: string) => void;
    onMoreReactions: (anchorEl: HTMLElement) => void;
    onReply: () => void;
    onQuoteReply?: (quote: string) => void;
    onCopyText: () => void;
    onEdit: () => void;
    onPin: () => void;
    onForward: () => void;
    onDelete: () => void;
    onRestore: () => void;
    onSelect: () => void;
    onSaveAttachment: (attId: number) => void;
    onCopyImage: (attId: number) => void;
    onJumpToProfile: (userId: string) => void;
  };
  /** Доп. данные о message: read_by + кто реагировал */
  readByEnabled: boolean;
  avatarSrc: (filename: string | null | undefined, userId: string) => string;
}

// ДВА отдельных слоя:
// - dimLayer: затенение фона (z-index 900). Под активным сообщением (1000).
// - menuLayer: pill + menu (z-index 1200). Над активным сообщением.
// Между ними — активное сообщение (через position:fixed + z-index 1000).
let dimLayer: HTMLDivElement | null = null;
let menuLayer: HTMLDivElement | null = null;
let pill: HTMLDivElement | null = null;
let menu: HTMLDivElement | null = null;
let currentMessageId: number | null = null;
let activeBubbleEl: HTMLElement | null = null;
let activeBubbleOriginalTransform: string = "";
let bubbleClone: HTMLElement | null = null;  // visual clone в menuLayer (escapes stacking)

function ensureDom() {
  if (dimLayer) return;
  // СЛОЙ 1: затенение фона (z-index 900)
  dimLayer = document.createElement("div");
  dimLayer.className = "chat-context-dim-layer";
  // touch-action:none — гарантия что dim ЗАХВАТЫВАЕТ все жесты, не пропуская их
  // вниз к чату (иначе тап в пустое место открывал фото в lightbox).
  dimLayer.style.cssText = "position:fixed;inset:0;z-index:900;display:none;background:transparent;touch-action:none;";
  // Все события на dim → закрытие меню, никакого пропускания вниз
  const dimClose = (e: Event) => {
    e.stopPropagation();
    e.preventDefault();
    closeContextMenu();
  };
  dimLayer.addEventListener("pointerdown", dimClose);
  dimLayer.addEventListener("touchstart", dimClose, { passive: false });
  dimLayer.addEventListener("touchend", dimClose, { passive: false });
  dimLayer.addEventListener("click", dimClose);
  dimLayer.addEventListener("contextmenu", (e) => e.preventDefault());
  document.body.appendChild(dimLayer);

  // СЛОЙ 2: меню (z-index 1200) — поверх всего
  menuLayer = document.createElement("div");
  menuLayer.className = "chat-context-overlay";
  menuLayer.style.cssText = "position:fixed;inset:0;z-index:1200;display:none;background:transparent;pointer-events:none;";
  menuLayer.addEventListener("click", (e) => {
    if (e.target === menuLayer) closeContextMenu();
  });
  menuLayer.addEventListener("contextmenu", (e) => e.preventDefault());
  document.body.appendChild(menuLayer);

  pill = document.createElement("div");
  pill.className = "chat-context-emoji-pill";
  pill.style.pointerEvents = "auto";
  menuLayer.appendChild(pill);

  menu = document.createElement("div");
  menu.className = "chat-context-menu";
  menu.style.pointerEvents = "auto";
  menuLayer.appendChild(menu);
}

function makeButton(className: string, contentHtml: string, onTap: () => void): HTMLButtonElement {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = className;
  btn.innerHTML = contentHtml;
  let fired = false;
  const fire = (e: Event) => {
    if (fired) return;
    fired = true;
    e.stopPropagation();
    e.preventDefault();
    closeContextMenu();
    setTimeout(() => onTap(), 0);
    setTimeout(() => { fired = false; }, 600);
  };
  // Tap-detection: запоминаем позицию touchstart, фаерим только если
  // touchend на том же месте (без движения = тап, с движением = скролл).
  // touchstart напрямую тапал слишком агрессивно — мешал скроллу меню.
  let touchStartX = 0, touchStartY = 0, touchMoved = false;
  btn.addEventListener("touchstart", (e) => {
    const t = e.touches[0];
    touchStartX = t.clientX;
    touchStartY = t.clientY;
    touchMoved = false;
  }, { passive: true });
  btn.addEventListener("touchmove", (e) => {
    const t = e.touches[0];
    if (Math.abs(t.clientX - touchStartX) > 8 || Math.abs(t.clientY - touchStartY) > 8) {
      touchMoved = true;
    }
  }, { passive: true });
  btn.addEventListener("touchend", (e) => {
    if (!touchMoved) fire(e);
  });
  // Pointerdown как fallback для не-touch (мышь/перо) — там скролла нет
  btn.addEventListener("pointerdown", (e) => {
    if ((e as PointerEvent).pointerType !== "touch") fire(e);
  });
  // click как фолбэк (если touchend не сработал)
  btn.addEventListener("click", fire);
  // Не показывать нативное контекст-меню при ПКМ
  btn.addEventListener("contextmenu", (e) => e.preventDefault());
  return btn;
}

function escapeHtml(s: string): string {
  const map: Record<string, string> = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" };
  return s.replace(/[&<>"']/g, (c) => map[c]!);
}

function iconHtml(name: string, size: number = 14): string {
  const icons: Record<string, string> = {
    reply: '<svg width="' + size + '" height="' + size + '" viewBox="0 0 16 16" fill="none"><path d="M3 6L7 2M3 6L7 10M3 6H10C12 6 13 7 13 9V14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    save: '<svg width="' + size + '" height="' + size + '" viewBox="0 0 16 16" fill="none"><path d="M3 11V13H13V11M8 3V11M8 11L4 7M8 11L12 7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    download: '<svg width="' + size + '" height="' + size + '" viewBox="0 0 16 16" fill="none"><path d="M8 2V10M8 10L4.5 6.5M8 10L11.5 6.5M2 13H14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    edit: '<svg width="' + size + '" height="' + size + '" viewBox="0 0 16 16" fill="none"><path d="M11 2L14 5L5 14L2 14L2 11L11 2Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    pin: '<svg width="' + size + '" height="' + size + '" viewBox="0 0 16 16" fill="none"><path d="M5 2L11 2L10 6L13 9L8 9L8 14L7 9L3 9L6 6Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/></svg>',
    external: '<svg width="' + size + '" height="' + size + '" viewBox="0 0 16 16" fill="none"><path d="M10 2H14V6M14 2L8 8M9 4H4C2.9 4 2 4.9 2 6V12C2 13.1 2.9 14 4 14H10C11.1 14 12 13.1 12 12V7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    trash: '<svg width="' + size + '" height="' + size + '" viewBox="0 0 16 16" fill="none"><path d="M3 4H13M5 4V13C5 13.5 5.5 14 6 14H10C10.5 14 11 13.5 11 13V4M6 4V2H10V4M7 7V11M9 7V11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>',
    check: '<svg width="' + size + '" height="' + size + '" viewBox="0 0 16 16" fill="none"><path d="M3 8L6 11L13 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    copy: '<svg width="' + size + '" height="' + size + '" viewBox="0 0 16 16" fill="none"><rect x="5" y="5" width="9" height="9" rx="1" stroke="currentColor" stroke-width="1.5"/><path d="M11 5V3C11 2.4 10.6 2 10 2H3C2.4 2 2 2.4 2 3V10C2 10.6 2.4 11 3 11H5" stroke="currentColor" stroke-width="1.5"/></svg>',
    chevron_d: '<svg width="' + size + '" height="' + size + '" viewBox="0 0 16 16" fill="none"><path d="M4 6L8 10L12 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    restore: '<svg width="' + size + '" height="' + size + '" viewBox="0 0 16 16" fill="none"><path d="M3 8a5 5 0 1 1 1.5 3.5M3 8V5M3 8H6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  };
  return '<span class="chat-context-item-icon">' + (icons[name] || '') + '</span>';
}

export function openContextMenu(opts: VanillaMenuOpenOpts) {
  ensureDom();
  if (!dimLayer || !menuLayer || !pill || !menu) return;

  currentMessageId = opts.message.id;
  const m = opts.message;
  const cb = opts.callbacks;

  if (opts.isMobile) menuLayer.classList.add("is-mobile-sheet");
  else menuLayer.classList.remove("is-mobile-sheet");
  menuLayer.setAttribute("data-open", "true");
  menuLayer.style.display = "";
  dimLayer.style.display = "";

  // На ПК — БЕЗ затемнения, blur'а и клонирования bubble. Просто меню+pill
  // около курсора. Dim/clone-эффекты — только мобильные (как в TG mobile).
  if (opts.isMobile) {
    dimLayer.style.background = "rgba(0, 0, 0, 0.32)";
    dimLayer.style.backdropFilter = "blur(6px)";
    (dimLayer.style as any).webkitBackdropFilter = "blur(6px)";
  } else {
    dimLayer.style.background = "transparent";
    dimLayer.style.backdropFilter = "none";
    (dimLayer.style as any).webkitBackdropFilter = "none";
  }

  pill.style.top = opts.position.pillTop + "px";
  pill.style.left = opts.position.pillLeft + "px";
  // Emoji-pill реакций не нужен для удалённого сообщения — скрываем.
  pill.style.display = m.deleted_at ? "none" : "";
  menu.style.top = opts.position.menuTop + "px";
  menu.style.left = opts.position.menuLeft + "px";
  menu.style.maxHeight = opts.position.menuMaxHeight + "px";

  activeBubbleEl = document.getElementById("chat-msg-" + m.id);
  if (activeBubbleEl && opts.isMobile) {
    // Bubble lift+clone — только на мобиле (на ПК bubble остаётся на месте,
    // визуально не выделяется — меню достаточно).
    const rect = activeBubbleEl.getBoundingClientRect();
    bubbleClone = activeBubbleEl.cloneNode(true) as HTMLElement;
    bubbleClone.id = activeBubbleEl.id + "__clone";
    bubbleClone.style.cssText =
      "position:fixed;" +
      "top:" + (rect.top + opts.position.liftOffset) + "px;" +
      "left:" + rect.left + "px;" +
      "width:" + rect.width + "px;" +
      "z-index:1000;pointer-events:none;margin:0;";
    document.body.appendChild(bubbleClone);
    activeBubbleEl.classList.add("is-active-ctx");
    activeBubbleOriginalTransform = activeBubbleEl.style.cssText;
    activeBubbleEl.style.visibility = "hidden";
  }
  const feedEl = document.querySelector(".chat-feed") as HTMLElement | null;
  if (feedEl) feedEl.classList.add("has-active-ctx");

  pill.innerHTML = "";
  for (const emo of opts.quickEmoji) {
    const myReaction = m.reactions?.find((r) => r.emoji === emo && r.mine);
    // Сразу <img> без native — иначе native эмодзи флэшит до twemoji.parse.
    const btn = makeButton(
      "chat-context-emoji" + (myReaction ? " is-active" : ""),
      emojiImgHtml(emo),
      () => cb.onReact(emo)
    );
    pill.appendChild(btn);
  }
  const moreBtn = makeButton(
    "chat-context-emoji is-more",
    iconHtml("chevron_d", 14),
    () => cb.onMoreReactions(moreBtn)
  );
  moreBtn.title = "Больше реакций";
  pill.appendChild(moreBtn);

  menu.innerHTML = "";
  const aid = opts.attachmentId;

  // Удалённое сообщение — минимальное меню: только Восстановить + Выбрать.
  // (reply/edit/pin/forward/delete/реакции бессмысленны для удалённого.)
  if (m.deleted_at) {
    if (opts.canRestore) {
      menu.appendChild(makeButton(
        "chat-context-item",
        iconHtml("restore") + "Восстановить",
        () => cb.onRestore()
      ));
    }
    menu.appendChild(makeButton(
      "chat-context-item",
      iconHtml("check") + "Выбрать",
      () => cb.onSelect()
    ));
  } else {

  if (aid != null) {
    const att = m.attachments?.find((a: ChatAttachment) => a.id === aid);
    if (att) {
      const mime = (att.mime || "").toLowerCase();
      const isVideo = mime.startsWith("video/");
      const saveLabel = isVideo
        ? "Скачать видео"
        : (window.innerWidth < 900 ? "Сохранить к себе" : "Сохранить как…");
      menu.appendChild(makeButton(
        "chat-context-item",
        iconHtml(isVideo ? "download" : "save") + saveLabel,
        () => cb.onSaveAttachment(aid)
      ));
      if (mime.startsWith("image/")) {
        menu.appendChild(makeButton(
          "chat-context-item",
          iconHtml("copy") + "Скопировать img",
          () => cb.onCopyImage(aid)
        ));
      }
      const div = document.createElement("div");
      div.className = "chat-context-divider";
      menu.appendChild(div);
    }
  }

  menu.appendChild(makeButton(
    "chat-context-item",
    iconHtml("reply") + "Ответить",
    () => cb.onReply()
  ));

  if (opts.quotedText && cb.onQuoteReply) {
    const quote = opts.quotedText;
    menu.appendChild(makeButton(
      "chat-context-item",
      iconHtml("reply") + "Цитировать",
      () => cb.onQuoteReply!(quote)
    ));
  }

  if (m.text) {
    menu.appendChild(makeButton(
      "chat-context-item",
      iconHtml("save") + "Скопировать",
      () => cb.onCopyText()
    ));
  }

  if (opts.canEditMine) {
    menu.appendChild(makeButton(
      "chat-context-item",
      iconHtml("edit") + "Изменить",
      () => cb.onEdit()
    ));
  }

  menu.appendChild(makeButton(
    "chat-context-item",
    iconHtml("pin") + (m.pinned_at ? "Открепить" : "Закрепить"),
    () => cb.onPin()
  ));

  menu.appendChild(makeButton(
    "chat-context-item",
    iconHtml("external") + "Переслать",
    () => cb.onForward()
  ));

  if (opts.canDelete) {
    menu.appendChild(makeButton(
      "chat-context-item is-danger",
      iconHtml("trash") + "Удалить",
      () => cb.onDelete()
    ));
  }

  menu.appendChild(makeButton(
    "chat-context-item",
    iconHtml("check") + "Выбрать",
    () => cb.onSelect()
  ));

  }  // ← конец ветки "не удалённое сообщение"

  // === Секция «Прочитали» (для своих не удалённых сообщений) ===
  const readBy = (m as any).read_by as Array<{ user_id: string; display_name: string; avatar_filename: string | null }> | undefined;
  if (opts.readByEnabled && readBy && readBy.length > 0 && !m.deleted_at) {
    const div1 = document.createElement("div");
    div1.className = "chat-context-divider";
    menu.appendChild(div1);

    const info = document.createElement("div");
    info.className = "chat-context-info";
    info.innerHTML = `
      <div class="chat-context-info-head">
        <span class="chat-context-item-icon">${iconHtml("check", 12).replace('<span class="chat-context-item-icon">', '').replace("</span>", "")}</span>
        Прочитали (${readBy.length})
      </div>
    `;
    const peopleWrap = document.createElement("div");
    peopleWrap.className = "chat-context-people";
    for (const u of readBy) {
      const btn = makeButton(
        "chat-context-person",
        `<img src="${escapeHtml(opts.avatarSrc(u.avatar_filename, u.user_id))}" alt="" style="width:22px;height:22px;border-radius:50%;object-fit:cover"><span>${escapeHtml(u.display_name)}</span>`,
        () => cb.onJumpToProfile(u.user_id)
      );
      peopleWrap.appendChild(btn);
    }
    info.appendChild(peopleWrap);
    menu.appendChild(info);
  }

  // === Секция «Реакции» (если есть) ===
  const reactions = m.reactions as Array<{ emoji: string; count: number; users?: Array<{ user_id: string; display_name: string; avatar_filename: string | null }> }> | undefined;
  if (!m.deleted_at && reactions && reactions.length > 0) {
    const div2 = document.createElement("div");
    div2.className = "chat-context-divider";
    menu.appendChild(div2);

    const info2 = document.createElement("div");
    info2.className = "chat-context-info";
    const reactList = document.createElement("div");
    reactList.className = "chat-context-reactions-list";
    for (const r of reactions) {
      const row = document.createElement("div");
      row.className = "chat-context-reaction-row";
      row.innerHTML = `
        <span class="chat-context-reaction-emoji-big">${emojiImgHtml(r.emoji)}</span>
        <span class="chat-context-reaction-num">${r.count}</span>
      `;
      const stack = document.createElement("div");
      stack.className = "chat-context-avatars-stack";
      const users = r.users || [];
      for (let i = 0; i < Math.min(5, users.length); i++) {
        const u = users[i];
        const avatarUrl = opts.avatarSrc(u.avatar_filename, u.user_id);
        const initial = (u.display_name || "?").trim().charAt(0).toUpperCase();
        // Простой hash-based gradient для fallback
        const palette = ["#4fae4e","#3390ec","#9c7ce5","#00acc1","#e85c5c","#e99a12"];
        const hash = (u.user_id || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0);
        const bg = palette[hash % palette.length];
        const inner = u.avatar_filename && avatarUrl
          ? `<img src="${escapeHtml(avatarUrl)}" alt="" style="width:22px;height:22px;border-radius:50%;object-fit:cover">`
          : `<span style="width:22px;height:22px;border-radius:50%;background:${bg};color:#fff;display:inline-flex;align-items:center;justify-content:center;font-size:11px;font-weight:600;">${escapeHtml(initial)}</span>`;
        const ab = makeButton(
          "chat-context-avatar-link",
          inner,
          () => cb.onJumpToProfile(u.user_id)
        );
        ab.title = u.display_name;
        stack.appendChild(ab);
      }
      if (users.length > 5) {
        const more = document.createElement("span");
        more.className = "chat-context-avatars-more";
        more.textContent = `+${users.length - 5}`;
        stack.appendChild(more);
      }
      row.appendChild(stack);
      reactList.appendChild(row);
    }
    info2.appendChild(reactList);
    menu.appendChild(info2);
  }

  // Конвертируем native emoji в Twemoji <img> (TG-style)
  if (menuLayer) {
    try { twemoji.parse(menuLayer, { folder: "svg", ext: ".svg", base: "/twemoji/" }); } catch (e) { /* ignore */ }
  }
}

export function closeContextMenu() {
  if (!menuLayer || !dimLayer) return;
  menuLayer.setAttribute("data-open", "false");
  menuLayer.style.display = "none";
  dimLayer.style.display = "none";
  if (bubbleClone) {
    bubbleClone.remove();
    bubbleClone = null;
  }
  if (activeBubbleEl) {
    activeBubbleEl.classList.remove("is-active-ctx");
    activeBubbleEl.style.cssText = activeBubbleOriginalTransform;
    activeBubbleEl = null;
  }
  // Defensive: убрать любые "висящие" клоны/fixed-bubble (если refetch
  // пере-маунтил DOM и reference потерялся)
  document.querySelectorAll('[id$="__clone"]').forEach((el) => el.remove());
  document.querySelectorAll(".chat-msg").forEach((el) => {
    const elem = el as HTMLElement;
    if (elem.style.visibility === "hidden" || elem.style.position === "fixed") {
      elem.style.cssText = "";
      elem.classList.remove("is-active-ctx");
    }
  });
  const feedEl = document.querySelector(".chat-feed") as HTMLElement | null;
  if (feedEl) feedEl.classList.remove("has-active-ctx");
  currentMessageId = null;
}

export function isMenuOpen(): boolean {
  return menuLayer !== null && menuLayer.style.display !== "none";
}



export function getCurrentMessageId(): number | null {
  return currentMessageId;
}


// === Generic bottom-sheet menu (для других модалок: media gallery, etc) ===

export interface SimpleSheetItem {
  /** icon name из iconHtml() — reply/save/edit/pin/external/trash/check/copy/chevron_d */
  icon: string;
  label: string;
  onTap: () => void;
  danger?: boolean;
}

/**
 * Простой bottom-sheet без pill/lift bubble — список кнопок снизу экрана.
 * Используется для media-gallery context menu и т.п.
 */
export function openSimpleSheet(opts: { items: SimpleSheetItem[]; isMobile?: boolean; activeEl?: HTMLElement | null; x?: number; y?: number }) {
  ensureDom();
  if (!dimLayer || !menuLayer || !pill || !menu) return;

  // Скрываем pill (он только для chat ctx menu)
  pill.style.display = "none";

  const isMobile = opts.isMobile !== false;
  menuLayer.classList.toggle("is-mobile-sheet", isMobile);
  menuLayer.setAttribute("data-open", "true");
  menuLayer.style.display = "";
  dimLayer.style.display = "";

  // На ПК — без затемнения и blur (только меню)
  if (isMobile) {
    dimLayer.style.background = "rgba(0, 0, 0, 0.32)";
    dimLayer.style.backdropFilter = "blur(6px)";
    (dimLayer.style as any).webkitBackdropFilter = "blur(6px)";
  } else {
    dimLayer.style.background = "transparent";
    dimLayer.style.backdropFilter = "none";
    (dimLayer.style as any).webkitBackdropFilter = "none";
    // На ПК позиционируем menu около курсора с clamp'ом по viewport
    const x = opts.x ?? 0;
    const y = opts.y ?? 0;
    const MENU_W = 240;  // примерная ширина (см. CSS .chat-context-menu)
    const MENU_H_EST = Math.min(60 + opts.items.length * 40, 480);
    const vw = window.innerWidth, vh = window.innerHeight;
    const left = Math.max(8, Math.min(x, vw - MENU_W - 8));
    const top = Math.max(8, Math.min(y, vh - MENU_H_EST - 8));
    menu.style.top = top + "px";
    menu.style.left = left + "px";
    menu.style.right = "auto";
    menu.style.bottom = "auto";
    menu.style.maxHeight = (vh - top - 16) + "px";
  }

  // Lift активный элемент над dim (clone + visibility:hidden оригинал) —
  // только на мобиле, чтобы long-pressed item был виден поверх затемнения
  if (opts.activeEl && isMobile) {
    activeBubbleEl = opts.activeEl;
    const rect = activeBubbleEl.getBoundingClientRect();
    bubbleClone = activeBubbleEl.cloneNode(true) as HTMLElement;
    bubbleClone.id = (activeBubbleEl.id || "sheet-active") + "__clone";
    bubbleClone.style.cssText =
      "position:fixed;" +
      "top:" + rect.top + "px;" +
      "left:" + rect.left + "px;" +
      "width:" + rect.width + "px;" +
      "height:" + rect.height + "px;" +
      "z-index:1000;pointer-events:none;margin:0;";
    document.body.appendChild(bubbleClone);
    activeBubbleOriginalTransform = activeBubbleEl.style.cssText;
    activeBubbleEl.style.visibility = "hidden";
  }

  // Build menu items
  menu.innerHTML = "";
  for (const item of opts.items) {
    const cls = "chat-context-item" + (item.danger ? " is-danger" : "");
    menu.appendChild(makeButton(cls, iconHtml(item.icon) + escapeHtml(item.label), item.onTap));
  }
}

export function closeSimpleSheet() {
  if (pill) pill.style.display = "";  // вернуть для следующего chat ctx menu
  closeContextMenu();
}
